import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Loader2, RefreshCcw } from 'lucide-react';
import useProjectStore from '../../stores/projectStore';
import useWorkflowStore from '../../stores/workflowStore';
import ContractSidebar from './ContractSidebar';
import Contract00View from './Contract00View';
import Contract0XView from './Contract0XView';
import ResearchPathSelector from './ResearchPathSelector';
import Contract01View from './Contract01View';
import Contract01bView from './Contract01bView';
import Contract015View from './Contract015View';
import Contract02View from './Contract02View';
import Contract03View from './Contract03View';
import Contract04View from './Contract04View';
import Contract05View from './Contract05View';
import Contract06View from './Contract06View';
import Contract07View from './Contract07View';
import Contract08View from './Contract08View';
import Contract09View from './Contract09View';
import Contract095View from './Contract095View';
import Contract10View from './Contract10View';
import Contract11View from './Contract11View';
import Contract12View from './Contract12View';
import Contract13View from './Contract13View';
import Contract14AView from './Contract14AView';
import Contract14BView from './Contract14BView';
import Contract15View from './Contract15View';
import Contract16View from './Contract16View';
import Contract162View from './Contract162View';
import Contract163View from './Contract163View';
import Contract164View from './Contract164View';
import Contract165View from './Contract165View';

/**
 * WorkflowPage - Pagina principale per il workflow dei contracts (0-26)
 */
const WorkflowPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  // Stores
  const { currentProject, loadProject } = useProjectStore();
  const {
    currentContract,
    executions,
    selectedExecution,
    loading,
    executing,
    error,
    initWorkflow,
    loadExecutions,
    executeContract,
    approveExecution,
    rejectExecution,
    regenerateContract,
    checkAIStatus,
    checkVeoStatus,
    submitVeoJob,
    extendVeoJob,
    updateVeoClip,
    deleteVeoClip,
    regenerateVeoClip,
    discardVeoVideo,
    resubmitVeo,
    rebuildVeoJobs,
    resetContractsFrom,
    navigateToContract,
    selectExecution,
    setSelectedExecution,
    reset
  } = useWorkflowStore();

  const [initializing, setInitializing] = useState(true);

  // Determina se C16.4 ha bisogno di una nuova esecuzione per caricare i dati da C16.3.
  // Scatta se non ci sono executions, oppure se non esiste nessuna execution 'approved'
  // né una 'completed' con backendPayload valido (caso: execution vuota creata prima che
  // C16.3 fosse approvato).
  const needsC164Execute = (execs) => {
    if (!execs || execs.length === 0) return true;
    if (execs.some(e => e.status === 'approved')) return false;
    // 'awaiting_veo_submit' è lo status normale di C16.4 dopo execute() — va trattato come valido.
    // Controlla anche che variants sia non vuoto: se è 0 significa che C16.3.2 ha avuto un bug
    // e bisogna ricreare l'execution con la re-segmentazione inline di prepareInput().
    return !execs.some(
      e => (e.status === 'completed' || e.status === 'awaiting_veo_submit') &&
           e.inputData?.backendPayload &&
           Object.keys(e.inputData.backendPayload).length > 0 &&
           (e.inputData.backendPayload.generation_options?.variants?.length || 0) > 0
    );
  };

  // Per C16.4 privilegia: 1) approved più recente (ha veo_jobs), 2) completed più recente con dati validi.
  // Per gli altri contract usa la logica standard (async → withOutput → primo).
  const selectBestExecution = (execs, contractNum) => {
    if (contractNum === 16.4) {
      const sorted = [...execs].sort((a, b) => (b.executionNumber || 0) - (a.executionNumber || 0));
      return sorted.find(e => e.status === 'approved') ||
             sorted.find(e => e.status === 'completed' && Object.keys(e.inputData?.backendPayload || {}).length > 0) ||
             sorted[0];
    }
    const asyncPending = execs.find(e => e.status === 'executing' && e.openaiResponseId);
    const withOutput   = execs.find(e => e.status !== 'executing' || e.outputData || e.output);
    return asyncPending || withOutput || execs[0];
  };

  /**
   * Inizializza il workflow al mount
   */
  useEffect(() => {
    const init = async () => {
      try {
        setInitializing(true);

        // Carica il progetto
        const project = await loadProject(projectId);

        // Inizializza il workflow
        await initWorkflow(projectId, project);

        // Carica le executions del contract corrente
        if (project.currentContract !== null) {
          let execs = await loadExecutions(project.currentContract);

          // NOTA: per C16.4 NON chiamiamo executeContract qui — lo gestisce
          // loadContractExecutions (triggerato da initWorkflow sopra). Evita la race
          // condition in cui entrambi gli effect chiamano executeContract concorrentemente.

          if (execs && execs.length > 0) {
            const best = selectBestExecution(execs, project.currentContract);
            // Per C16.4 usiamo setSelectedExecution diretto (i dati sono già completi)
            // Per altri contract usiamo selectExecution che fa un fetch individuale
            if (project.currentContract === 16.4) {
              setSelectedExecution(best);
            } else {
              await selectExecution(project.currentContract, best._id);
            }
          }
        }
      } catch (err) {
        console.error('Error initializing workflow:', err);
      } finally {
        setInitializing(false);
      }
    };

    init();

    // Cleanup al unmount
    return () => {
      reset();
    };
  }, [projectId]);

  /**
   * Carica executions quando cambia il contract corrente
   */
  useEffect(() => {
    const loadContractExecutions = async () => {
      if (currentContract !== null && currentContract !== undefined && !isNaN(currentContract)) {
        try {
          let execs = await loadExecutions(currentContract);

          // Contract 16.4 è human-input: crea un'execution al volo se non ne esiste una
          // valida (es. execution vuota creata prima che C16.3 fosse approvato).
          if (currentContract === 16.4 && needsC164Execute(execs)) {
            await executeContract(16.4);
            execs = await loadExecutions(16.4);
          }

          if (execs && execs.length > 0) {
            const best = selectBestExecution(execs, currentContract);
            if (currentContract === 16.4) {
              setSelectedExecution(best);
            } else {
              await selectExecution(currentContract, best._id);
            }
          }
        } catch (err) {
          console.error('Error loading contract executions:', err);
        }
      }
    };

    loadContractExecutions();
  }, [currentContract]);

  /**
   * Handler per selezione contract
   */
  const handleContractSelect = (contractNum) => {
    navigateToContract(contractNum);
  };

  /**
   * Handler per esecuzione contract
   */
  const handleExecute = async () => {
    try {
      await executeContract(currentContract);
    } catch (err) {
      console.error('Error executing contract:', err);
    }
  };

  /**
   * Handler per approvazione
   */
  const handleApprove = async (curatedOutput = null) => {
    try {
      let execToApprove = selectedExecution;

      // Contract 16.2 e 16.4 sono human-input: se non esiste ancora un'execution la crea al volo
      if (!execToApprove && (currentContract === 16.2 || currentContract === 16.4)) {
        execToApprove = await executeContract(currentContract);
      }

      if (!execToApprove) return;

      await approveExecution(currentContract, execToApprove._id, null, curatedOutput);

      // Ricarica il progetto per aggiornare lo stato
      await loadProject(projectId);
    } catch (err) {
      console.error('Error approving execution:', err);
    }
  };

  /**
   * Handler per rigetto
   */
  const handleReject = async (reason) => {
    if (!selectedExecution) return;
    
    try {
      await rejectExecution(currentContract, selectedExecution._id, reason);
    } catch (err) {
      console.error('Error rejecting execution:', err);
    }
  };

  /**
   * Ricarica C16.4: cerca un'esecuzione con dati validi tra quelle esistenti
   * oppure ne crea una nuova da C16.3. Usa setSelectedExecution per aggiornare
   * la vista direttamente senza un secondo roundtrip network.
   */
  const handleReloadC164 = async () => {
    try {
      // Prima prova a trovare una buona execution già esistente
      let execs = await loadExecutions(16.4);
      let best = selectBestExecution(execs, 16.4);

      const hasValidData = best &&
        best.inputData?.backendPayload &&
        Object.keys(best.inputData.backendPayload).length > 0;

      if (!hasValidData) {
        // Nessuna execution con dati validi: creane una nuova da C16.3
        await executeContract(16.4);
        execs = await loadExecutions(16.4);
        best = selectBestExecution(execs, 16.4);
      }

      if (best) {
        setSelectedExecution(best);
      }
    } catch (err) {
      console.error('Error reloading C16.4:', err);
    }
  };

  /**
   * Handler per rigenerazione
   */
  const handleRegenerate = async () => {
    if (!selectedExecution) return;

    try {
      await regenerateContract(currentContract, selectedExecution._id);
    } catch (err) {
      console.error('Error regenerating contract:', err);
    }
  };

  /**
   * Scelta percorso di ricerca: Standard (C1+C1B) oppure Alternativo (C0X).
   * Mostrato dopo l'approvazione di C0, prima che C1 venga eseguito.
   */
  const handleSelectStandardPath = async () => {
    try {
      await executeContract(1);
    } catch (err) {
      console.error('Error executing C1:', err);
    }
  };

  const handleSelectAlternativePath = async () => {
    try {
      navigateToContract(0.1);
      await executeContract(0.1);
    } catch (err) {
      console.error('Error executing C0X:', err);
    }
  };

  /**
   * Handler per controllo stato job asincrono
   */
  const handleCheckAIStatus = async () => {
    try {
      const result = await checkAIStatus(currentContract);
      if (result.status === 'in_progress') {
        alert('Il job OpenAI è ancora in elaborazione. Riprova tra qualche minuto.');
      } else if (result.status === 'failed') {
        alert('Il job è andato perso dopo il riavvio del server. L\'esecuzione è stata marcata come fallita — puoi rieseguire il contract.');
      }
    } catch (err) {
      console.error('Error checking AI status:', err);
    }
  };

  /**
   * Rendering condizionale per loading iniziale
   */
  if (initializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Caricamento workflow...</p>
        </div>
      </div>
    );
  }

  /**
   * Rendering condizionale per errore
   */
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-pink-50 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-800">Errore</h2>
            </div>
            <p className="text-red-700 mb-4">{error}</p>
            <button
              onClick={() => navigate('/projects')}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Torna ai Progetti
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fallback resiliente per C16.4: se selectedExecution è null (reset transitorio)
  // usa la migliore execution già caricata in executions[16.4].
  const effectiveC164Execution = currentContract === 16.4
    ? (selectedExecution || selectBestExecution(executions[16.4] || [], 16.4) || null)
    : selectedExecution;

  const contractExecutions = executions[currentContract] || [];
  const hasActiveExecution = contractExecutions.some(e =>
    e.status === 'completed' || e.status === 'approved' || e.status === 'executing'
  );
  const hasAsyncPending = contractExecutions.some(e =>
    e.status === 'executing' && e.openaiResponseId
  );

  // C0 completato: vera se completedContracts nel progetto lo include (dopo refresh)
  // OPPURE se executions[0] ha una execution approved (subito dopo l'approvazione, prima del reload)
  const c0Completed =
    (currentProject?.completedContracts || []).includes(0) ||
    (executions[0] || []).some(e => e.status === 'approved');

  // Mostra il selettore del percorso quando siamo su C1, non ci sono ancora execution attive,
  // e C0 risulta completato (indipendentemente dalla fonte del dato)
  const showPathSelector = currentContract === 1 && !hasActiveExecution && c0Completed;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-indigo-50 via-white to-pink-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/projects')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-800">
                {currentProject?.name || 'Caricamento...'}
              </h1>
              <p className="text-sm text-gray-500">
                Contract {currentContract} di 26 • {currentProject?.status || 'N/A'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Check AI Status Button — visibile solo se c'è un job async in attesa */}
            {hasAsyncPending && (
              <button
                onClick={handleCheckAIStatus}
                disabled={loading}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Controllo...
                  </>
                ) : (
                  <>
                    <RefreshCcw className="w-4 h-4" />
                    Controlla se pronto
                  </>
                )}
              </button>
            )}

            {/* Execute Button — nascosto per human-input (16.2, 16.4) e per il path selector */}
            {!hasActiveExecution && currentContract !== 16.2 && currentContract !== 16.4 &&
              !showPathSelector && (
              <button
                onClick={handleExecute}
                disabled={executing}
                className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-pink-600 text-white rounded-lg hover:from-indigo-700 hover:to-pink-700 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {executing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Esecuzione...
                  </>
                ) : (
                  'Esegui Contract'
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <ContractSidebar
          currentContract={currentContract}
          completedContracts={currentProject?.completedContracts || []}
          onContractSelect={handleContractSelect}
          onResetFrom={resetContractsFrom}
          className="w-80 flex-shrink-0"
        />

        {/* Contract View Area */}
        <div className="flex-1 overflow-hidden relative">
          {/* Async pending overlay — mostrato quando ChatGPT sta elaborando */}
          {selectedExecution?.status === 'executing' && selectedExecution?.openaiResponseId && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 backdrop-blur-sm">
              <div className="text-center p-8 max-w-md">
                <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
                <p className="text-lg font-semibold text-gray-800 mb-2">ChatGPT sta elaborando...</p>
                <p className="text-sm text-gray-500 mb-6">
                  Il job è stato inviato in background. Quando sarà pronto, clicca il pulsante in alto per scaricare il risultato.
                </p>
                <button
                  onClick={handleCheckAIStatus}
                  disabled={loading}
                  className="px-5 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-all disabled:opacity-50 flex items-center gap-2 mx-auto"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                  {loading ? 'Controllo...' : 'Controlla se pronto'}
                </button>
              </div>
            </div>
          )}

          {currentContract === 0 ? (
            <Contract00View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 0.1 ? (
            <Contract0XView
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : showPathSelector ? (
            <ResearchPathSelector
              onSelectStandard={handleSelectStandardPath}
              onSelectAlternative={handleSelectAlternativePath}
              executing={executing}
            />
          ) : currentContract === 1 ? (
            <Contract01View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 1.1 ? (
            <Contract01bView
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 1.5 ? (
            <Contract015View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 2 ? (
            <Contract02View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 3 ? (
            <Contract03View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 4 ? (
            <Contract04View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 5 ? (
            <Contract05View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 6 ? (
            <Contract06View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 7 ? (
            <Contract07View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 8 ? (
            <Contract08View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 9 ? (
            <Contract09View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 9.5 ? (
            <Contract095View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 10 ? (
            <Contract10View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 11 ? (
            <Contract11View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 12 ? (
            <Contract12View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              loading={loading}
            />
          ) : currentContract === 13 ? (
            <Contract13View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 14.1 ? (
            <Contract14AView
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 14.2 ? (
            <Contract14BView
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 15 ? (
            <Contract15View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 16 ? (
            <Contract16View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 16.1 ? (
            <Contract165View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 16.2 ? (
            <Contract162View
              execution={selectedExecution}
              onApprove={handleApprove}
              loading={loading || executing}
            />
          ) : currentContract === 16.3 ? (
            <Contract163View
              execution={selectedExecution}
              onApprove={handleApprove}
              onReject={handleReject}
              onRegenerate={handleRegenerate}
              loading={loading}
            />
          ) : currentContract === 16.4 ? (
            <Contract164View
              execution={effectiveC164Execution}
              onApprove={handleApprove}
              onSubmitVeoJob={submitVeoJob}
              onExtendVeoJob={extendVeoJob}
              onUpdateVeoClip={updateVeoClip}
              onDeleteVeoClip={deleteVeoClip}
              onRegenerateVeoClip={regenerateVeoClip}
              onCheckVeoStatus={checkVeoStatus}
              onDiscardVeo={discardVeoVideo}
              onResubmitVeo={resubmitVeo}
              onRebuildJobs={rebuildVeoJobs}
              onReload={handleReloadC164}
              loading={loading || executing}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <p className="text-lg mb-2">Contract {currentContract}</p>
                <p className="text-sm">View non ancora implementata</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowPage;
